import React, { useMemo, useState } from "react";
import { Check, Upload } from "lucide-react";

interface ApproveRepaymentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (file?: File | null) => void;
  repayments: {
    repaymentHistoryId: string;
    customerName?: string;
    repaymentAmount: number;
  }[];
  loading?: boolean;
}

const ApproveRepaymentModal: React.FC<ApproveRepaymentModalProps> = ({
  open,
  onClose,
  onConfirm,
  repayments,
  loading = false,
}) => {
  const totalAmount = useMemo(
    () => repayments.reduce((sum, item) => sum + item.repaymentAmount, 0),
    [repayments],
  );

  const [proofFile, setProofFile] = useState<File | null>(null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-6 w-6 text-emerald-600" />
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600 max-w-sm mx-auto">
            You are about to approve{" "}
            <span className="font-medium text-gray-900">
              {repayments.length}
            </span>{" "}
            repayment{repayments.length > 1 ? "s" : ""}. Please upload proof to
            proceed.
          </p>
        </div>

        {repayments.length > 0 ? (
          <div className="mt-6">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Selected repayments ({repayments.length})
            </p>

            <div className="max-h-56 overflow-y-auto rounded-md border bg-gray-50">
              {repayments.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between px-3 py-2 border-b last:border-b-0"
                >
                  <span className="text-sm text-gray-700 truncate">
                    {item.customerName || "Unknown Customer"}
                  </span>

                  <span className="text-sm font-semibold text-emerald-600">
                    ₹{item.repaymentAmount.toLocaleString("en-IN")}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-3 flex justify-between px-2 text-sm">
              <span className="text-gray-600">Grand total</span>
              <span className="font-semibold text-gray-900">
                ₹{totalAmount.toLocaleString("en-IN")}
              </span>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Repayment Proof <span className="text-red-500">*</span>
              </label>

              <label className="flex items-center gap-3 px-3 py-2 border border-dashed rounded-md cursor-pointer hover:bg-gray-50">
                <Upload className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600 truncate">
                  {proofFile
                    ? proofFile.name
                    : "Click to upload file (PDF / Image)"}
                </span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      setProofFile(e.target.files[0]);
                    }
                  }}
                />
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Max file size: 5MB. Supported formats: PDF, JPG, PNG
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 text-sm text-slate-500 text-center">
            No repayments selected
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => {
              setProofFile(null);
              onClose();
            }}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={() => onConfirm(proofFile)}
            disabled={loading || !proofFile}
            className="px-4 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {loading ? "Processing..." : "Confirm & Approve"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApproveRepaymentModal;
